<template>
  <b-modal
    ref="courseCertificateModal"
    hide-footer
    title="Generate Course Certificate"
    centered
    lazy
    @hide="hideModal"
    size="lg"
  >
    <small v-html="modalDescription"></small>

    <div class="text-center my-4" v-if="isLoading">
      <b-spinner class="align-middle"></b-spinner>
    </div>

    <div class="text-center my-4 text-70" v-else-if="!courses.length">
      <md-icon class="text-50" style="font-size: 40px;">school</md-icon>
      <p class="mb-0 mt-2">This student has not completed any courses yet.</p>
    </div>

    <b-table-simple v-else responsive class="mt-3">
      <b-thead>
        <b-tr>
          <b-th>Course</b-th>
          <b-th>Completed</b-th>
          <b-th>Certificate</b-th>
          <b-th class="text-right">Action</b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr v-for="entry in courses" :key="entry.course">
          <b-td>{{ entry.course_title }}</b-td>
          <b-td>{{ formatDate(entry.completed_at) }}</b-td>
          <b-td>
            <span v-if="entry.certificate" class="text-success">
              {{ entry.certificate.certificate_number }}
            </span>
            <span v-else class="text-50">Not generated</span>
          </b-td>
          <b-td class="text-right">
            <button
              class="btn btn-sm btn-primary mr-2"
              :disabled="busyCourse === entry.course"
              @click.prevent="handleGenerate(entry)"
            >
              <b-spinner class="align-middle mr-1" small v-if="busyCourse === entry.course"></b-spinner>
              <span v-else>{{ entry.certificate ? 'Regenerate' : 'Generate' }}</span>
            </button>
            <button
              v-if="entry.certificate && entry.certificate.pdf_url"
              class="btn btn-sm btn-secondary"
              @click.prevent="handleDownload(entry.certificate)"
            >
              <md-icon>print</md-icon>
            </button>
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { get } from 'lodash';

export default {
  name: 'CourseCertificateModal',
  props: {
    showModal: { type: Boolean, default: false },
    student: { type: Object, default: () => {} },
  },
  data() {
    return {
      courses: [],
      isLoading: false,
      busyCourse: null,
    };
  },
  watch: {
    showModal(value) {
      if (value) {
        this.fetchEligibleCourses();
        this.$refs.courseCertificateModal.show();
      }
    },
  },
  computed: {
    modalDescription() {
      if (!this.student) return '';
      return `Generate the <b>Completion of Classroom Training</b> certificate for
        <b>${get(this.student, 'user.first_name', '')} ${get(this.student, 'user.last_name', '')}</b>.
        Only completed courses are listed.`;
    },
  },
  methods: {
    ...mapActions('courseEnrollments', ['getCertificateEligibleCourses', 'generateCourseCertificate']),
    ...mapActions('fileDownload', ['downloadFile']),
    get,

    formatDate(val) {
      if (!val) return '-';
      try {
        return new Date(val).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
        });
      } catch (e) {
        return val;
      }
    },

    async fetchEligibleCourses() {
      if (!get(this.student, 'id')) return;
      this.isLoading = true;
      try {
        const response = await this.getCertificateEligibleCourses({ student: this.student.id });
        this.courses = get(response, 'data', []) || [];
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    async handleGenerate(entry) {
      this.busyCourse = entry.course;
      try {
        const response = await this.generateCourseCertificate({
          student: this.student.id,
          course: entry.course,
        });
        const certificate = get(response, 'data');
        this.courses = this.courses.map(course =>
          course.course === entry.course ? { ...course, certificate } : course,
        );
        this.makeToast({ variant: 'success', msg: 'Certificate generated.' });
        if (get(certificate, 'pdf_url')) {
          this.handleDownload(certificate);
        }
      } catch (err) {
        this.makeToast({
          variant: 'danger',
          msg: get(err, 'response.data.message') || this.$t('generalMsgs.genErrorMsg'),
        });
      }
      this.busyCourse = null;
    },

    async handleDownload(certificate) {
      try {
        await this.downloadFile({ fileUrl: certificate.pdf_url, removeTimestamp: true });
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    hideModal() {
      this.courses = [];
      this.$emit('close', true);
    },
  },
};
</script>
